<template>
  <div class="sidebar" style="padding-top: 3.5em; overflow-y: auto">
    <div class="panelmenu-wrapper">
      <PanelMenu :model="visibleMenuItems" style="width: 100%; margin-top: 1.5em" />
    </div>
    <div class="logout-wrapper">
      <PanelMenu :model="logoutItem" style="width: 100%" />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, nextTick, onMounted, onBeforeUnmount } from "vue";
import { useStoreCompat, useRouterCompat } from "@/composables";

const store = useStoreCompat();
const { router } = useRouterCompat();
const isDevMode = process.env.VUE_APP_VUE_MODE === "development";
const isTesting = process.env.VUE_APP_BACKEND_ENVIRONMENT?.includes("siistesting.electro3.com.ar");

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  userType: {
    type: String,
    default: "",
  },
});

const buildNewActuacionDestination = (to) => {
  // Transforma rutas del tipo /actuaciones/new/:flow/initial -> /newActuacion?type=:flow
  // Solo habilitado en desarrollo (VUE_APP_VUE_MODE=development)
  const isDevShortcut = process.env.VUE_APP_VUE_MODE === "development";
  if (isDevShortcut && typeof to === "string") {
    const m = to.match(/^\/actuaciones\/new\/([^/]+)\/initial$/);
    if (m) {
      return { path: "/newActuacion", query: { type: m[1] } };
    }
  }
  return to;
};

const logout = async () => {
  store.dispatch("sumarioList/resetActuaciones");
  await store.dispatch("user/logout");
  router.push("/");
  location.reload();
};

const handleLinkClick = async (item) => {
  if (item.label === "Departamentales") {
    window.open(
      "https://docs.google.com/spreadsheets/d/1jFAq_uItOydo0KU6DqWcXi-XNsY0OaRj539-LfwmjEQ/edit?usp=sharing"
    );
    return;
  }

  await store.dispatch("sumario/resetState");

  if (item.label === "Cerrar Sesión") {
    await logout();
    return;
  }

  if (item.to) {
    const dest = buildNewActuacionDestination(item.to);

    if (typeof dest === "string") {
      if (router.currentRoute.path !== dest) router.push(dest);
    } else if (dest && typeof dest === "object") {
      const samePath = router.currentRoute.path === dest.path;
      const sameFlow = router.currentRoute.query?.type === dest.query?.type;
      if (!samePath || !sameFlow) router.push(dest);
    }
  }
};

const userRole = computed(() => props.userType);

const syncActiveLinkClass = async () => {
  await nextTick();

  const wrapper = document.querySelector(".panelmenu-wrapper");
  if (!wrapper) return;

  wrapper.querySelectorAll("a.menu-active-dom").forEach((anchor) => {
    anchor.classList.remove("menu-active-dom");
  });

  const currentPath = router.currentRoute.path || "";
  const currentType = router.currentRoute.query?.type || "";
  if (!currentPath.startsWith("/newActuacion") || !currentType) return;

  wrapper.querySelectorAll("a[href*='type=']").forEach((anchor) => {
    const href = anchor.getAttribute("href");
    if (!href) return;

    const parsed = new URL(href, window.location.origin);
    const isNewActuacionMenuLink = parsed.pathname.startsWith("/newActuacion");
    const sameType = parsed.searchParams.get("type") === currentType;

    if (isNewActuacionMenuLink && sameType) {
      anchor.classList.add("menu-active-dom");
    }
  });
};

function filterByRole(items, role) {
  return items
    .map((item) => {
      const newItem = { ...item };

      if (newItem.items) {
        newItem.items = filterByRole(newItem.items, role);
      }

      const excludedRoles = Array.isArray(newItem.excludesRoles) ? newItem.excludesRoles : [];
      const allowedRoles = Array.isArray(newItem.roles) ? newItem.roles : [];
      const isDevOnly = !!newItem.devOnly;

      if (isDevOnly && !isDevMode && !isTesting) {
        return null;
      }

      const isExcluded = excludedRoles.includes(role);
      const hasAccess = !isExcluded && (allowedRoles.length === 0 || allowedRoles.includes(role));

      const hasVisibleChildren = newItem.items && newItem.items.length > 0;

      return hasAccess || hasVisibleChildren ? newItem : null;
    })
    .filter(Boolean);
}

function addCommandToMenu(items) {
  return items.map((item) => {
    const newItem = { ...item };

    if (newItem.to) {
      newItem.to = buildNewActuacionDestination(newItem.to);
    }

    if (newItem.items) {
      newItem.items = addCommandToMenu(newItem.items);
    }

    if (newItem.to || newItem.label === "Departamentales" || newItem.label === "Cerrar Sesión") {
      newItem.command = () => handleLinkClick(newItem);
    }

    return newItem;
  });
}

const menuItems = ref([
  {
    label: "Escritorio",
    icon: "pi pi-chart-bar",
    to: "/",
    roles: [],
    excludesRoles: ["Calabocero"],
  },
  {
    label: "Listado de Actuaciones",
    icon: "pi pi-list",
    to: "/actuaciones/list",
    roles: [
      "User Tester",
      "Super User",
      "Jefe de Dependencia",
      "Oficial Sumariante",
      "Jefe de Sumario",
      "UsuarioDependencia",
      "Control de Gestion",
      "Dirección de Información para el Delito D-2",
      "Dpto. Asuntos Judiciales D-5",
      "Dpto. Planeamiento",
      "Juez",
      "Ayudante de Juez",
      "Dpto.Criminalistica",
      "Lab. Informatico Forense",
      "Administrativo",
      "Ayudante Fiscal",
      "Fiscal",
      "Fiscal General",
      "Policia Investigador",
      "Supervisor",
      "UsuarioDepartamental",
      "Notificante - Departamental",
    ],
  },
  {
    label: "Sumarios",
    icon: "pi pi-file",
    roles: [
      "User Tester",
      "Super User",
      "Jefe de Dependencia",
      "Oficial Sumariante",
      "Jefe de Sumario",
    ],
    items: [
      {
        label: "Sumario Denuncia",
        to: "/actuaciones/new/sumario-denuncia/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Sumario Oficio",
        to: "/actuaciones/new/sumario-oficio/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
    ],
  },
  {
    label: "Expedientes",
    icon: "pi pi-folder",
    roles: [
      "User Tester",
      "Super User",
      "Jefe de Dependencia",
      "Oficial Sumariante",
      "Jefe de Sumario",
    ],
    items: [
      {
        label: "Expediente Denuncia",
        to: "/actuaciones/new/expediente-denuncia/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Expediente Oficio",
        to: "/actuaciones/new/expediente-oficio/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
    ],
  },
  {
    label: "UFI delitos",
    icon: "pi pi-desktop",
    roles: [
      "User Tester",
      "Super User",
      "Jefe de Dependencia",
      "Oficial Sumariante",
      "Jefe de Sumario",
    ],
    items: [
      {
        label: "Flagrancia",
        to: "/actuaciones/new/ufi-flagrancia/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Contra la Propiedad por Denuncia",
        to: "/actuaciones/new/ufi-propiedad-denuncia/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Contra la Propiedad por Oficio",
        to: "/actuaciones/new/ufi-propiedad-oficio/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Informáticos y Estafas por Denuncia",
        to: "/actuaciones/new/ufi-informatica-denuncia/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Informáticos y Estafas por Oficio",
        to: "/actuaciones/new/ufi-informatica-oficio/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Cavig",
        to: "/actuaciones/new/ufi-cavig/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Anivi",
        to: "/actuaciones/new/ufi-anivi/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Genéricos",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
        items: [
          {
            label: "Genéricos por Denuncia",
            to: "/actuaciones/new/ufi-generica-denuncia/initial",
            roles: [
              "User Tester",
              "Super User",
              "Jefe de Dependencia",
              "Oficial Sumariante",
              "Jefe de Sumario",
            ],
          },
          {
            label: "Genéricos por Oficio",
            to: "/actuaciones/new/ufi-generica-oficio/initial",
            roles: [
              "User Tester",
              "Super User",
              "Jefe de Dependencia",
              "Oficial Sumariante",
              "Jefe de Sumario",
            ],
          },
          {
            label: "Establecer Paradero",
            to: "/actuaciones/new/ufi-establecer-paradero/initial",
            roles: [
              "User Tester",
              "Super User",
              "Jefe de Dependencia",
              "Oficial Sumariante",
              "Jefe de Sumario",
            ],
          },
        ],
      },
      {
        label: "Especiales",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
        items: [
          {
            label: "Emergencias Medicas",
            to: "/actuaciones/new/ufi-emergencias-medicas/initial",
            roles: [
              "User Tester",
              "Super User",
              "Jefe de Dependencia",
              "Oficial Sumariante",
              "Jefe de Sumario",
            ],
          },
          {
            label: "Siniestro Vial",
            to: "/actuaciones/new/ufi-siniestro-vial/initial",
            roles: [
              "User Tester",
              "Super User",
              "Jefe de Dependencia",
              "Oficial Sumariante",
              "Jefe de Sumario",
            ],
          },
          {
            label: "Ahorcamiento o Suicidio",
            to: "/actuaciones/new/ufi-suicidio/initial",
            roles: [
              "User Tester",
              "Super User",
              "Jefe de Dependencia",
              "Oficial Sumariante",
              "Jefe de Sumario",
            ],
          },
          {
            label: "Incendio en Vivienda",
            to: "/actuaciones/new/ufi-incendio-vivienda/initial",
            roles: [
              "User Tester",
              "Super User",
              "Jefe de Dependencia",
              "Oficial Sumariante",
              "Jefe de Sumario",
            ],
          },
          {
            label: "Intoxicación",
            to: "/actuaciones/new/ufi-intoxicacion/initial",
            roles: [
              "User Tester",
              "Super User",
              "Jefe de Dependencia",
              "Oficial Sumariante",
              "Jefe de Sumario",
            ],
          },
          {
            label: "Otros",
            to: "/actuaciones/new/ufi-otros/initial",
            roles: [
              "User Tester",
              "Super User",
              "Jefe de Dependencia",
              "Oficial Sumariante",
              "Jefe de Sumario",
            ],
          },
        ],
      },
    ],
  },
  {
    label: "Preliminares",
    icon: "pi pi-file-excel",
    roles: [
      "User Tester",
      "Super User",
      "Jefe de Dependencia",
      "Oficial Sumariante",
      "Jefe de Sumario",
    ],
    items: [
      {
        label: "Incendio en vivienda",
        to: "/actuaciones/new/preliminares-incendio-vivienda/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Establecer procedencia",
        to: "/actuaciones/new/preliminares-establecer-procedencia/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Fuga de menor",
        to: "/actuaciones/new/preliminares-fuga-menor/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Siniestro vial",
        to: "/actuaciones/new/preliminares-siniestro-vial/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Caída casual",
        to: "/actuaciones/new/preliminares-caida-casual/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Incendio de automotor",
        to: "/actuaciones/new/preliminares-incendio-automotor/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Tentativa de suicidio",
        to: "/actuaciones/new/preliminares-suicidio/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
      {
        label: "Otros",
        to: "/actuaciones/new/preliminares-otros/initial",
        roles: [
          "User Tester",
          "Super User",
          "Jefe de Dependencia",
          "Oficial Sumariante",
          "Jefe de Sumario",
        ],
      },
    ],
  },
  {
    label: "Depósito",
    icon: "pi pi-bars",
    roles: [
      "User Tester",
      "Super User",
      "Deposito Judicial",
      "Jefe de Dependencia",
      "UsuarioDepartamental",
    ],
    items: [
      {
        label: "Efectos",
        to: "/deposito/efectos",
        roles: [
          "User Tester",
          "Super User",
          "Deposito Judicial",
          "Jefe de Dependencia",
          "UsuarioDepartamental",
        ],
      },
      {
        label: "Solicitudes",
        to: "/deposito/solicitudes",
        roles: [
          "User Tester",
          "Super User",
          "Deposito Judicial",
          "Jefe de Dependencia",
          "UsuarioDepartamental",
        ],
      },
    ],
  },
  {
    label: "Parametrización",
    icon: "pi pi-sliders-v",
    to: "/parametros/list",
    roles: ["User Tester", "Super User"],
  },
  {
    label: "Biblioteca Virtual",
    icon: "pi pi-search",
    to: "/bibliotecaVirtual",
    roles: [
      "Super User",
      "UsuarioDepartamental",
      "Jefe de Dependencia",
      "Jefe de Sumario",
      "Oficial Sumariante",
    ],
  },
  {
    label: "Localización",
    icon: "pi pi-map-marker",
    to: "/location",
    roles: ["Jefe de Dependencia", "Jefe de Sumario", "Oficial Sumariante", "Super User"],
  },
  {
    label: "Calabocero",
    icon: "pi pi-shield",
    to: "/calabozo",
    roles: ["Calabocero", "Super User"],
  },
  {
    label: "Suba de imagenes",
    icon: "pi pi-upload",
    to: "/subida-imagenes",
    roles: ["Super User"],
    devOnly: true,
  },
  {
    label: "SIFCOP",
    icon: "pi pi-id-card",
    to: "/sifcop",
    roles: ["Super User"],
    devOnly: true,
  },
  {
    label: "Cambiar Contraseña",
    icon: "pi pi-unlock",
    to: "/editar-contrasena",
  },
  {
    label: "Notificaciones",
    icon: "pi pi-bell",
    to: "/notificaciones",
    roles: ["Notificante", "Notificante - Departamental"],
  },
  {
    label: "Departamentales",
    icon: "pi pi-file",
    to: "/",
    roles: ["Notificante"],
  },
]);

defineExpose({ menuItems });

const visibleMenuItems = ref([]);

let removeAfterEachHook = null;

const refreshVisibleMenu = () => {
  const filtered = filterByRole(menuItems.value, userRole.value);
  visibleMenuItems.value = addCommandToMenu(filtered);
};

watch(userRole, refreshVisibleMenu, { immediate: true });

onMounted(() => {
  removeAfterEachHook = router.afterEach(() => {
    syncActiveLinkClass();
  });

  refreshVisibleMenu();
  syncActiveLinkClass();
});

onBeforeUnmount(() => {
  if (typeof removeAfterEachHook === "function") {
    removeAfterEachHook();
  }
});

const logoutItem = ref([
  {
    label: "Cerrar Sesión",
    icon: "pi pi-sign-out",
    command: () => logout(),
  },
]);
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap");

.sidebar {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.custom-menuitem {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Pinta de azul los items principales, y las letras de blanco, cambia la fuente y quita los bordes líneas blancas del Panel Menu */
>>> .p-panelmenu .p-panelmenu-header > a {
  background: #0b5195;
  color: #fff !important;
  border: none !important;
  font-family: "Inter", sans-serif !important;
}

/* Cambia de color el hover de los items principales */
>>> .p-panelmenu .p-panelmenu-header > a:hover {
  background: #d9d9d94d !important;
}

/* SUB MENU */
>>> .p-panelmenu .p-panelmenu-content {
  background: #0b5195 !important;
  border: none;
}

>>> .p-panelmenu .p-panelmenu-content .p-menuitem .p-menuitem-link .p-menuitem-text {
  color: #fff !important;
}

/* Pone los iconos flecha a la derecha */
>>> .p-panelmenu .p-panelmenu-header > a .p-panelmenu-icon {
  position: absolute;
  right: 0;
}

/* Quitan las marcas celestes de los hover */
>>> [data-v-08b9c012] .p-panelmenu .p-panelmenu-header > a:focus {
  box-shadow: none !important;
  background-color: #d9d9d94d !important;
}

>>> .p-panelmenu .p-panelmenu-content .p-menuitem .p-menuitem-link:not(.p-disabled):focus {
  box-shadow: none !important;
  background-color: #d9d9d94d !important;
}

/* Activo (router) + Activo (DOM para newActuacion?type=) */
>>> .router-link-active,
>>> .router-link-active-exact {
  background-color: #d9d9d94d !important;
}

>>> .p-panelmenu a.menu-active-dom {
  background-color: #d9d9d94d !important;
}

>>> .p-panelmenu .p-panelmenu-header.p-highlight > a {
  background-color: #d9d9d94d !important;
}

>>> .p-panelmenu .p-panelmenu-content .p-menuitem .p-menuitem-link:not(.p-disabled):hover {
  color: #fff !important;
  background: #d9d9d94d !important;
}

>>> .p-panelmenu .p-panelmenu-content .p-menuitem .p-menuitem-link {
  color: #fff !important;
}
</style>
